import type {
  Maybe,
  TimeMaterialsBudgetItemInvoiceData,
} from "@src/__generated__/graphql";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import { denominate, HUNDRED_PERCENT, nominate } from "@src/utils/formatters";
import { FieldState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";
import type { ChangeEvent } from "react";
import type { TimeAndMaterialsBudgetItem } from "./table-state";

export class TimeAndMaterialsBudgetItemForm implements BaseStore {
  appStore;

  id: string;
  title: string;
  invoiced: number;
  amount: number;
  default_commission: Maybe<number>;
  budgetCategoryId: TimeAndMaterialsBudgetItem["budget_category_id"];
  @observable amountToInvoice: FieldState<number | undefined>;
  @observable previousAmountToInvoice: number;
  @observable commission?: FieldState<number | undefined>;
  @observable
  __subRows: TimeMaterialsBudgetItemInvoiceData[] = [];

  constructor(budgetItem: TimeAndMaterialsBudgetItem, appStore: AppStore) {
    makeObservable(this);
    this.appStore = appStore;

    this.id = budgetItem.id;
    this.title = budgetItem.title;
    this.amount = budgetItem.amount;
    this.invoiced = budgetItem.invoiced;
    this.budgetCategoryId = budgetItem.budget_category_id;
    this.amountToInvoice = new FieldState<number | undefined>(0);
    this.previousAmountToInvoice = 0;
    this.default_commission = budgetItem.default_commission;

    if (!budgetItem.data) return;
    this.setSubRows(budgetItem.data);
  }

  @computed get amountInputValue() {
    return this.getNominatedAmount(this.amountToInvoice.value);
  }

  @computed get commissionInputValue() {
    if (!this.commission) return "";
    return this.getNominatedAmount(this.commission.value, 2);
  }

  @action
  setSubRows(items: Maybe<TimeMaterialsBudgetItemInvoiceData>[]) {
    this.__subRows = [];
    const itemCount = items.length;

    for (let i = 0; i < itemCount; i++) {
      const item = items[i];
      if (!item) continue;
      this.__subRows.push(item);
    }
  }

  @action setAmountToInvoice(newAmount = 0, rememberPreviousState?: boolean) {
    this.amountToInvoice.reset(newAmount);

    if (!rememberPreviousState) return;
    this.previousAmountToInvoice = newAmount;
  }

  @action setCommission(commission: Maybe<number>, makeDirty?: boolean) {
    if (typeof commission !== "number") {
      this.commission = undefined;
      return;
    }
    this.commission = new FieldState<number | undefined>(commission);
    if (!makeDirty) return;
    this.commission.dirty = true;
  }

  @action.bound handleAmountInputChange(event: ChangeEvent<HTMLInputElement>) {
    const newAmount = this.getDenominatedAmount(Number(event.target.value));
    this.amountToInvoice.onChange(newAmount);
    this.previousAmountToInvoice = this.subtractPercent(
      newAmount ?? 0,
      this.commission?.$ ?? 0,
    );
  }

  @action.bound handleCommissionInputChange(
    event: ChangeEvent<HTMLInputElement>,
    selectedIds: string[] | undefined,
  ) {
    const commission = this.getDenominatedAmount(Number(event.target.value), 2);

    // NOTE: To keep old dirty state
    if (this.amountToInvoice.dirty) {
      this.amountToInvoice.onChange(
        this.addPercent(this.previousAmountToInvoice, commission ?? 0),
      );
    } else {
      this.setAmountToInvoice(
        this.addPercent(
          this.getSelectedAmount(selectedIds ?? []),
          commission ?? 0,
        ),
      );
    }

    this.commission?.onChange(commission);
  }

  isRowOrChildSelected(selectedIds: string[] | undefined): boolean {
    if (!selectedIds) return false;
    return (
      selectedIds.includes(this.id) ||
      this.__subRows.some((subRow) => selectedIds.includes(subRow.id))
    );
  }

  getSelectedAmount(selectedIds: string[]): number {
    if (selectedIds.includes(this.id)) {
      return this.amount;
    }

    let amount = 0;

    for (const subRow of this.__subRows) {
      if (!selectedIds.includes(subRow.id)) continue;
      amount += subRow.amount ?? 0;
    }

    return amount;
  }

  addPercent(amount: number, percent: number): number {
    return Math.round(amount * (1 + percent / HUNDRED_PERCENT));
  }

  private subtractPercent(amount: number, percent: number): number {
    return Math.round((amount / (HUNDRED_PERCENT + percent)) * HUNDRED_PERCENT);
  }

  private getNominatedAmount(
    amount: number | undefined,
    nominator?: number,
  ): number {
    if (!amount) return 0;
    return nominate(
      amount,
      nominator ??
        this.appStore.workspaceStore.settings?.currency.denomination ??
        2,
    );
  }

  private getDenominatedAmount(
    amount: number | undefined,
    denominator?: number,
  ): number | undefined {
    if (!amount) return;
    return denominate(
      amount,
      denominator ??
        this.appStore.workspaceStore.settings?.currency.denomination ??
        2,
    );
  }
}
